import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'

import { borrarMejora, listaMejoras, pedirMejora } from '../api/mejora'
import {
  cambiarEnBiblioteca,
  obtenerCapitulos,
  obtenerManga,
  refrescarCapitulos,
  type Capitulo,
} from '../api/consultas'
import {
  borrarDescargado,
  descargarCapitulo,
  listarDescargados,
  type Progreso,
} from '../lib/offline'
import { comoDia } from '../lib/fechas'
import CambiarPortada from '../componentes/CambiarPortada'
import Finalizar from '../componentes/Finalizar'
import OtrasFuentes from '../componentes/OtrasFuentes'
import Recap from '../componentes/Recap'
import { fechaDeFinal, leerFinalizada } from '../lib/finalizadas'
import { IA_ACTIVA } from '../lib/ia'

/**
 * Orden en que se pintan los capitulos. La lista viene siempre del mas viejo
 * al mas nuevo (sourceOrder ascendente) y eso no se toca: de ahi salen el
 * boton de retomar y las descargas de "los siguientes". Esto solo cambia
 * como se ve. La preferencia se guarda porque uno lee siempre igual y no
 * quiere volver a tocarla en cada serie.
 */
type Orden = 'asc' | 'desc'

const CLAVE_ORDEN = 'yomou.ordenCapitulos'

function leerOrden(): Orden {
  return localStorage.getItem(CLAVE_ORDEN) === 'desc' ? 'desc' : 'asc'
}

export default function Manga() {
  const { id = '' } = useParams()
  const mangaId = Number(id)
  const clienteConsultas = useQueryClient()

  const { data: manga, error: errorManga } = useQuery({
    queryKey: ['manga', mangaId],
    queryFn: () => obtenerManga(mangaId),
    enabled: Number.isFinite(mangaId),
  })

  const {
    data: capitulos,
    isLoading: cargandoCapitulos,
    error: errorCapitulos,
  } = useQuery({
    queryKey: ['capitulos', mangaId],
    queryFn: () => obtenerCapitulos(mangaId),
    enabled: Number.isFinite(mangaId),
  })

  const alternarBiblioteca = useMutation({
    mutationFn: () => cambiarEnBiblioteca(mangaId, !manga?.inLibrary),
    onSuccess: () => {
      void clienteConsultas.invalidateQueries({ queryKey: ['manga', mangaId] })
      void clienteConsultas.invalidateQueries({ queryKey: ['biblioteca'] })
    },
  })

  const refrescar = useMutation({
    mutationFn: () => refrescarCapitulos(mangaId),
    onSuccess: () => clienteConsultas.invalidateQueries({ queryKey: ['capitulos', mangaId] }),
  })

  const { data: locales } = useQuery({
    queryKey: ['descargasLocales'],
    queryFn: listarDescargados,
  })

  const [orden, setOrden] = useState<Orden>(leerOrden)

  /** El mismo gesto de la rejilla, aqui como boton: no todo el mundo va a
   *  descubrir que se puede mantener pulsada una portada. */
  const [marcando, setMarcando] = useState(false)
  const finalizada = leerFinalizada(manga?.meta)

  function cambiarOrden() {
    const nuevo: Orden = orden === 'asc' ? 'desc' : 'asc'
    localStorage.setItem(CLAVE_ORDEN, nuevo)
    setOrden(nuevo)
  }

  const capitulosVisibles = useMemo(
    () => (orden === 'desc' ? [...(capitulos ?? [])].reverse() : (capitulos ?? [])),
    [capitulos, orden],
  )

  // Progreso de las descargas al telefono que estan en curso ahora mismo
  const [enMovil, setEnMovil] = useState<Map<number, Progreso>>(new Map())

  /*
   * Capitulos mejorados con waifu2x (ver `api/mejora`).
   *
   * Se pregunta por serie y no capitulo a capitulo: la ficha de One Piece
   * pinta mil doscientos, y el servicio solo devuelve los que tienen algo.
   * Mientras haya alguno en marcha se vuelve a preguntar cada pocos segundos,
   * que es lo que hace avanzar el porcentaje del boton.
   */
  const { data: mejoras } = useQuery({
    queryKey: ['mejoras', mangaId],
    queryFn: () => listaMejoras(mangaId),
    enabled: Number.isFinite(mangaId),
    retry: false,
    /*
     * Esto no se guarda en el aparato sino en el VPS, asi que lo que marcas en
     * el movil tiene que verse en el PC y al reves. Por eso se salta las dos
     * reglas generales de la app —cinco minutos de validez y no refrescar al
     * volver a la pestana—, que estan puestas para las consultas que van a las
     * fuentes externas: aqui la respuesta es de la maquina de al lado y
     * quedarse con una foto vieja es justo lo que confunde.
     */
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchOnMount: 'always',
    refetchInterval: (consulta) =>
      Object.values(consulta.state.data ?? {}).some(
        (m) => m.estado === 'cola' || m.estado === 'haciendo',
      )
        ? 4000
        : false,
  })

  const cambiarMejora = useMutation({
    mutationFn: async (orden: {
      capitulo: number
      paginas: number
      capituloId: number
      hecho: boolean
    }) => {
      if (orden.hecho) await borrarMejora(mangaId, orden.capitulo)
      else
        await pedirMejora(mangaId, orden.capitulo, Math.max(0, orden.paginas), orden.capituloId)
    },
    onSettled: () => clienteConsultas.invalidateQueries({ queryKey: ['mejoras', mangaId] }),
  })

  function marcarProgreso(id: number, progreso: Progreso | null) {
    setEnMovil((previo) => {
      const copia = new Map(previo)
      if (progreso) copia.set(id, progreso)
      else copia.delete(id)
      return copia
    })
  }

  async function bajarAlMovil(id: number) {
    marcarProgreso(id, { hechas: 0, total: 1 })
    try {
      await descargarCapitulo(id, (p) => marcarProgreso(id, p))
      await clienteConsultas.invalidateQueries({ queryKey: ['descargasLocales'] })
    } finally {
      marcarProgreso(id, null)
    }
  }

  const quitarDelMovil = useMutation({
    mutationFn: (id: number) => borrarDescargado(id),
    onSuccess: () => clienteConsultas.invalidateQueries({ queryKey: ['descargasLocales'] }),
  })

  /** Guarda de golpe los proximos capitulos sin leer, que es el caso real:
   *  te vas de viaje y quieres llevarte lo siguiente que toca. */
  async function bajarSiguientes(cuantos: number) {
    const pendientes = (capitulos ?? [])
      .filter((c) => !c.isRead && !locales?.some((l) => l.id === c.id))
      .slice(0, cuantos)
    for (const capitulo of pendientes) {
      await bajarAlMovil(capitulo.id)
    }
  }

  if (errorManga) {
    return (
      <div className="aviso error">
        <strong>No se pudo cargar</strong>
        <p>{(errorManga as Error).message}</p>
      </div>
    )
  }

  /*
   * Por donde retomar la lectura. Manda **cuando** leiste, no el orden.
   *
   * Antes se cogia el primer capitulo sin leer que tuviera progreso, o sea el
   * mas antiguo que hubieras dejado a medias. Cualquier capitulo que abrieras
   * un dia y cerraras se quedaba de trampa para siempre: yendo por el 105 de
   * Eternally Regressing Knight, el boton ofrecia "Continuar · Chapter 84",
   * que se dejo en la pagina 2 vete a saber cuando. Y hasta que se arreglo el
   * lector de tiras, abrir un capitulo ya dejaba progreso falso, asi que
   * trampas habia varias.
   *
   * Ahora se busca el ultimo capitulo que se toco. Si esta a medias, ahi se
   * vuelve; si se termino, al siguiente sin leer que venga despues.
   */
  const porFecha = capitulos?.reduce<Capitulo | undefined>((mejor, c) => {
    const cuando = Number(c.lastReadAt ?? 0)
    if (!cuando) return mejor
    return !mejor || cuando > Number(mejor.lastReadAt ?? 0) ? c : mejor
  }, undefined)

  /*
   * Plan B por orden, para cuando no hay fechas. Suwayomi no siempre guarda
   * `lastReadAt` —en One Piece no lo tiene ni un capitulo, con 12 abiertos—,
   * y sin este respaldo el boton mandaba al capitulo 0 de 1238.
   */
  const porOrden = capitulos?.reduce<Capitulo | undefined>((mejor, c) => {
    if (!c.isRead && c.lastPageRead <= 0) return mejor
    return !mejor || c.sourceOrder > mejor.sourceOrder ? c : mejor
  }, undefined)

  const ancla = porFecha ?? porOrden

  /*
   * Cuando ya estas al dia con lo publicado no hay "siguiente", y sin este
   * escalon el boton caia al capitulo 0 de 1238: en One Piece el ultimo
   * leido es el 1191, que es el ultimo que hay. Entonces lo util es volver al
   * capitulo que dejaste a medias, y de esos el mas reciente.
   */
  const rezagado = capitulos?.reduce<Capitulo | undefined>((mejor, c) => {
    if (c.isRead || c.lastPageRead <= 0) return mejor
    return !mejor || Number(c.lastReadAt ?? 0) > Number(mejor.lastReadAt ?? 0) ? c : mejor
  }, undefined)

  const aMedias = ancla && !ancla.isRead ? ancla : undefined
  const siguiente = ancla
    ? capitulos?.find((c) => !c.isRead && c.sourceOrder > ancla.sourceOrder)
    : undefined
  const retomar =
    aMedias ?? siguiente ?? rezagado ?? capitulos?.find((c) => !c.isRead) ?? capitulos?.[0]

  const leidos = capitulos?.filter((c) => c.isRead).length ?? 0
  const terminada = Boolean(capitulos?.length) && leidos === capitulos!.length

  function textoRetomar() {
    if (!retomar) return 'Leer'
    if (terminada) return `Releer · ${retomar.name}`
    // "Continuar" solo si de verdad se dejo empezado; abrirlo y salir no
    // cuenta, ahi se empieza desde el principio igual.
    if (!retomar.isRead && retomar.lastPageRead > 0) return `Continuar · ${retomar.name}`
    if (leidos > 0) return `Siguiente · ${retomar.name}`
    return `Empezar · ${retomar.name}`
  }

  return (
    <>
      <button className="volver" onClick={() => history.back()}>
        ‹ Atras
      </button>

      {manga && (
        <>
          <div className="cabecera-manga">
            <div style={{ width: 110, flexShrink: 0 }}>
              <CambiarPortada mangaId={mangaId} ruta={manga.thumbnailUrl} titulo={manga.title} />
            </div>
            <div>
              <h1 style={{ fontSize: 20 }}>{manga.title}</h1>
              {manga.author && <p className="meta">{manga.author}</p>}
              <p className="meta">
                {manga.source?.name}
                {manga.status ? ` · ${manga.status.toLowerCase()}` : ''}
              </p>
            </div>
          </div>

          {retomar && (
            <Link className="boton retomar" to={`/leer/${retomar.id}`}>
              <span>{textoRetomar()}</span>
              {retomar === aMedias && retomar.pageCount > 0 && (
                <small>
                  pagina {retomar.lastPageRead + 1} de {retomar.pageCount}
                </small>
              )}
            </Link>
          )}

          <div className="fila-acciones">
            <button
              className={manga.inLibrary ? 'boton secundario' : 'boton'}
              onClick={() => alternarBiblioteca.mutate()}
              disabled={alternarBiblioteca.isPending}
              style={{ flex: 1 }}
            >
              {manga.inLibrary ? '✓ En biblioteca' : '+ Anadir a biblioteca'}
            </button>
            {manga.inLibrary && (
              <button
                className={finalizada ? 'boton' : 'boton secundario'}
                onClick={() => setMarcando(true)}
                title={finalizada ? 'Esta en Finalizadas' : 'Marcar como finalizada'}
                style={{ whiteSpace: 'nowrap' }}
              >
                {finalizada ? '✓ Finalizada' : 'Finalizar'}
              </button>
            )}
          </div>

          {finalizada && (
            <p className="meta" style={{ marginTop: -6 }}>
              {finalizada.estado === 'abandonada' ? 'Abandonada' : 'Terminada'} el{' '}
              {comoDia(fechaDeFinal(finalizada))} ·{' '}
              <Link to="/finalizadas" className="enlace">
                ver en Finalizadas
              </Link>
            </p>
          )}

          {capitulos && capitulos.length > 0 && (
            <p className="meta" style={{ marginTop: -6 }}>
              {leidos} de {capitulos.length} capitulos leidos
            </p>
          )}

          {/* Acabas de leer el ultimo capitulo que hay: es justo el momento de
              preguntarlo, y no dos meses despues mirando la rejilla. */}
          {manga.inLibrary && terminada && !finalizada && capitulos!.length > 2 && (
            <div className="aviso">
              <strong>Te la has leido entera</strong>
              <p style={{ fontSize: 13 }}>
                Los {capitulos!.length} capitulos, leidos. Puedes guardarla en Finalizadas y dejar
                la biblioteca para lo que sigues.
              </p>
              <button className="boton" onClick={() => setMarcando(true)}>
                ✓ Marcar como finalizada
              </button>
            </div>
          )}

          {manga.description && <p className="descripcion">{manga.description}</p>}

          {manga.genre.length > 0 && (
            <div className="generos">
              {manga.genre.map((g) => (
                <span key={g} className="genero">
                  {g}
                </span>
              ))}
            </div>
          )}
        </>
      )}

      {IA_ACTIVA && <Recap mangaId={mangaId} leidos={leidos} />}

      <div className="fila-acciones" style={{ marginTop: 22 }}>
        <h2 style={{ fontSize: 16, margin: 0, flex: 1 }}>
          Capitulos {capitulos ? `(${capitulos.length})` : ''}
        </h2>
        {capitulos && capitulos.length > 1 && (
          <button
            className="boton secundario orden"
            onClick={cambiarOrden}
            title={
              orden === 'asc'
                ? 'Del primero al ultimo. Tocar para ver antes los nuevos'
                : 'Del ultimo al primero. Tocar para ver antes los antiguos'
            }
          >
            {orden === 'asc' ? '↑ Antiguos' : '↓ Nuevos'}
          </button>
        )}
        <button
          className="boton secundario"
          onClick={() => refrescar.mutate()}
          disabled={refrescar.isPending}
        >
          {refrescar.isPending ? 'Buscando…' : 'Refrescar'}
        </button>
      </div>

      {enMovil.size > 0 && (
        <div className="barra-descargas">
          <span className="girando">↻</span>
          <span style={{ flex: 1 }}>
            Guardando {enMovil.size} {enMovil.size === 1 ? 'capitulo' : 'capitulos'} en el movil
            <span className="meta">Podras leerlos sin conexion</span>
          </span>
        </div>
      )}

      {capitulos && capitulos.length > 0 && (
        <div className="fila-acciones" style={{ marginTop: 0 }}>
          <button
            className="boton secundario"
            style={{ flex: 1 }}
            onClick={() => void bajarSiguientes(5)}
            disabled={enMovil.size > 0}
          >
            ↓ Guardar 5 siguientes en el movil
          </button>
        </div>
      )}

      {cargandoCapitulos && <p className="cargando">Cargando capitulos…</p>}

      {errorCapitulos && (
        <div className="aviso error">
          <p>{(errorCapitulos as Error).message}</p>
        </div>
      )}

      {capitulos?.length === 0 && !cargandoCapitulos && manga && (
        <div className="aviso">
          <strong>Esta serie no tiene capitulos en {manga.source?.name ?? 'esta fuente'}</strong>
          <p style={{ fontSize: 13 }}>
            Pasa con las series licenciadas: MangaDex y otras retiran los capitulos oficiales por
            acuerdo con las editoriales y dejan solo la ficha. La serie no se ha perdido, esta en
            otro sitio.
          </p>
          <OtrasFuentes titulo={manga.title} fuenteActual={manga.source?.id} />
        </div>
      )}

      <ul className="lista">
        {capitulosVisibles.map((capitulo) => (
          <li key={capitulo.id}>
            <Link
              to={`/leer/${capitulo.id}`}
              className={
                [capitulo.isRead ? 'leido' : '', capitulo === aMedias ? 'en-curso' : '']
                  .filter(Boolean)
                  .join(' ') || undefined
              }
            >
              <span style={{ flex: 1 }}>
                {capitulo.name}
                <span className="meta">
                  {[
                    capitulo.scanlator,
                    comoDia(capitulo.uploadDate),
                    capitulo.lastPageRead > 0 && !capitulo.isRead
                      ? `por la pagina ${capitulo.lastPageRead + 1}`
                      : null,
                    capitulo.isRead ? 'leido' : null,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </span>
              </span>
              {(() => {
                /* Mejorar este capitulo. Al lado del de descargar porque es el
                   mismo gesto: eliges tu lo que vale la pena y el VPS se lo
                   curra en segundo plano, que tarda unos minutos. */
                const mejora = mejoras?.[capitulo.sourceOrder]
                const estado = mejora?.estado ?? 'nada'
                if (estado === 'cola' || estado === 'haciendo') {
                  const total = mejora?.total ?? 0
                  const hechas = mejora?.hechas ?? 0
                  return (
                    <span className="icono-accion en-descarga" title="Mejorando en el servidor">
                      {total > 0 ? `${Math.round((hechas / total) * 100)}%` : '…'}
                    </span>
                  )
                }
                const hecho = estado === 'lista'
                return (
                  <button
                    className={hecho ? 'icono-accion mejorado' : 'icono-accion'}
                    title={
                      hecho
                        ? 'Mejorado. Tocar para borrarlo del servidor'
                        : estado === 'fallo'
                          ? 'No se pudo mejorar. Tocar para reintentar'
                          : 'Mejorar la imagen de este capitulo (tarda unos minutos)'
                    }
                    onClick={(e) => {
                      e.preventDefault()
                      cambiarMejora.mutate({
                        capitulo: capitulo.sourceOrder,
                        paginas: capitulo.pageCount,
                        capituloId: capitulo.id,
                        hecho,
                      })
                    }}
                  >
                    {estado === 'fallo' ? '⚠' : '✨'}
                  </button>
                )
              })()}
              {(() => {
                const bajando = enMovil.get(capitulo.id)
                if (bajando) {
                  return (
                    <span className="icono-accion en-descarga" title="Guardando en el movil">
                      {Math.round((bajando.hechas / bajando.total) * 100)}%
                    </span>
                  )
                }

                const guardado = locales?.some((l) => l.id === capitulo.id)
                return (
                  <button
                    className={guardado ? 'icono-accion guardado' : 'icono-accion'}
                    title={
                      guardado
                        ? 'Guardado en el movil. Tocar para borrarlo'
                        : 'Guardar en el movil para leer sin conexion'
                    }
                    onClick={(e) => {
                      e.preventDefault()
                      if (guardado) quitarDelMovil.mutate(capitulo.id)
                      else bajarAlMovil(capitulo.id)
                    }}
                  >
                    {guardado ? '📱' : '↓'}
                  </button>
                )
              })()}
            </Link>
          </li>
        ))}
      </ul>

      {marcando && manga && (
        <Finalizar
          mangaId={mangaId}
          titulo={manga.title}
          finalizada={finalizada}
          conNota
          alCerrar={() => setMarcando(false)}
        />
      )}

      {manga && capitulos && capitulos.length > 0 && (
        <div className="pie-ficha">
          <p className="meta">
            Si esta fuente va lenta o le faltan capitulos, la serie puede estar mejor en otra.
          </p>
          <OtrasFuentes titulo={manga.title} fuenteActual={manga.source?.id} />
        </div>
      )}
    </>
  )
}
